import { useEffect, useState } from 'react';
import { adminApi } from '../../services/admin/admin.service';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Save, Upload, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

/* ═══════════════════════════════════
   Reusable inline edit components
   ═══════════════════════════════════ */

function SectionForm({ title, fields, data, onSave }: { title: string; fields: { key: string; label: string; type?: string }[]; data: any; onSave: (d: any) => Promise<void> }) {
    const [form, setForm] = useState<any>(data || {});
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (data) setForm(data); }, [data]);

    const handleSave = async () => {
        setSaving(true);
        try { await onSave(form); toast.success(`${title} salvo!`); } catch (e: any) { toast.error(e.message); } finally { setSaving(false); }
    };

    return (
        <Card className="mb-6">
            <CardHeader><CardTitle className="text-lg">{title}</CardTitle></CardHeader>
            <CardContent className="space-y-3">
                {fields.map(f => (
                    <div key={f.key}>
                        <label className="text-sm font-medium text-gray-600">{f.label}</label>
                        {f.type === 'textarea' ? (
                            <Textarea value={form[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })} className="mt-1" />
                        ) : (
                            <Input value={form[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })} className="mt-1" />
                        )}
                    </div>
                ))}
                <Button onClick={handleSave} disabled={saving} className="mt-2">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}Salvar
                </Button>
            </CardContent>
        </Card>
    );
}

function ItemsList({ title, items, fields, onSave, onDelete, parentId }: {
    title: string; items: any[]; fields: { key: string; label: string; type?: string }[];
    onSave: (d: any) => Promise<any>; onDelete: (id: number) => Promise<any>; parentId?: number;
}) {
    const [list, setList] = useState<any[]>(items || []);
    const [editId, setEditId] = useState<number | null>(null);
    const [form, setForm] = useState<any>({});
    const [saving, setSaving] = useState(false);

    useEffect(() => { setList(items || []); }, [items]);

    const startEdit = (item: any) => { setEditId(item.id); setForm({ ...item }); };
    const startNew = () => { setEditId(-1); setForm({ parent: parentId || 1, order: list.length }); };
    const cancel = () => { setEditId(null); setForm({}); };

    const save = async () => {
        setSaving(true);
        try {
            const result = await onSave(form);
            if (editId === -1) { setList([...list, result]); } else { setList(list.map(i => i.id === editId ? result : i)); }
            toast.success('Salvo!'); cancel();
        } catch (e: any) { toast.error(e.message); } finally { setSaving(false); }
    };

    const remove = async (id: number) => {
        if (!confirm('Remover este item?')) return;
        try { await onDelete(id); setList(list.filter(i => i.id !== id)); toast.success('Removido!'); } catch (e: any) { toast.error(e.message); }
    };

    return (
        <Card className="mb-4">
            <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">{title} ({list.length})</CardTitle>
                <Button size="sm" variant="outline" onClick={startNew}><Plus className="h-4 w-4 mr-1" />Novo</Button>
            </CardHeader>
            <CardContent>
                {list.map(item => (
                    <div key={item.id} className="border rounded-lg p-3 mb-2">
                        {editId === item.id ? (
                            <div className="space-y-2">
                                {fields.map(f => (
                                    <div key={f.key}>
                                        <label className="text-xs text-gray-500">{f.label}</label>
                                        {f.type === 'textarea' ? (
                                            <Textarea value={form[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })} className="mt-1" />
                                        ) : f.type === 'number' ? (
                                            <Input type="number" value={form[f.key] ?? ''} onChange={e => setForm({ ...form, [f.key]: Number(e.target.value) })} className="mt-1" />
                                        ) : (
                                            <Input value={form[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })} className="mt-1" />
                                        )}
                                    </div>
                                ))}
                                <div className="flex gap-2 mt-2">
                                    <Button size="sm" onClick={save} disabled={saving}>{saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Salvar'}</Button>
                                    <Button size="sm" variant="ghost" onClick={cancel}>Cancelar</Button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <p className="font-medium text-sm">{item[fields[0]?.key] || `Item #${item.id}`}</p>
                                    {fields[1] && <p className="text-xs text-gray-500 truncate max-w-lg">{item[fields[1]?.key]}</p>}
                                </div>
                                <div className="flex gap-1">
                                    <Button size="sm" variant="ghost" onClick={() => startEdit(item)}>Editar</Button>
                                    <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => remove(item.id)}><Trash2 className="h-4 w-4" /></Button>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
                {editId === -1 && (
                    <div className="border-2 border-dashed border-blue-200 rounded-lg p-3 mt-2 space-y-2">
                        {fields.map(f => (
                            <div key={f.key}>
                                <label className="text-xs text-gray-500">{f.label}</label>
                                {f.type === 'textarea' ? (
                                    <Textarea value={form[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })} className="mt-1" />
                                ) : f.type === 'number' ? (
                                    <Input type="number" value={form[f.key] ?? ''} onChange={e => setForm({ ...form, [f.key]: Number(e.target.value) })} className="mt-1" />
                                ) : (
                                    <Input value={form[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })} className="mt-1" />
                                )}
                            </div>
                        ))}
                        <div className="flex gap-2 mt-2">
                            <Button size="sm" onClick={save} disabled={saving}>{saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Salvar'}</Button>
                            <Button size="sm" variant="ghost" onClick={cancel}>Cancelar</Button>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

/* ═══════════════════════════════════
   Homepage Editor Page
   ═══════════════════════════════════ */

export default function HomepageEditor() {
    const [hero, setHero] = useState<any>(null);
    const [benefits, setBenefits] = useState<any>(null);
    const [faq, setFaq] = useState<any>(null);
    const [howItWorks, setHowItWorks] = useState<any>(null);
    const [maturity, setMaturity] = useState<any>(null);
    const [footer, setFooter] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        Promise.all([
            adminApi.getHero().then(setHero),
            adminApi.getBenefits().then(setBenefits),
            adminApi.getFaq().then(setFaq),
            adminApi.getHowItWorks().then(setHowItWorks),
            adminApi.getMaturityExplanation().then(setMaturity),
            adminApi.getFooter().then(setFooter),
        ]).catch(e => toast.error('Erro ao carregar dados: ' + e.message)).finally(() => setLoading(false));
    }, []);

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const result = await adminApi.uploadFile(file);
            toast.success('Upload realizado! URL: ' + result.url);
            navigator.clipboard.writeText(result.url);
            toast.info('URL copiada para a área de transferência');
        } catch (err: any) { toast.error(err.message); } finally { setUploading(false); }
    };

    if (loading) return <div className="flex items-center justify-center p-12"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;

    return (
        <div>
            <div className="flex items-center gap-3 mb-6">
                <Link to="/admin/dashboard"><ArrowLeft className="h-5 w-5 text-gray-500 hover:text-gray-800" /></Link>
                <h1 className="text-2xl font-bold">Editor da Homepage</h1>
            </div>

            {/* Upload */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-dashed">
                <label className="flex items-center gap-2 cursor-pointer">
                    <Upload className="h-5 w-5 text-gray-500" />
                    <span className="text-sm text-gray-600">Upload de imagem (a URL será copiada para uso nos campos abaixo)</span>
                    <input type="file" accept="image/*" className="hidden" onChange={handleUpload} />
                    {uploading && <Loader2 className="h-4 w-4 animate-spin" />}
                </label>
            </div>

            <Tabs defaultValue="hero">
                <TabsList className="mb-4 flex-wrap">
                    <TabsTrigger value="hero">Hero</TabsTrigger>
                    <TabsTrigger value="benefits">Benefícios</TabsTrigger>
                    <TabsTrigger value="faq">FAQ</TabsTrigger>
                    <TabsTrigger value="how">Como Funciona</TabsTrigger>
                    <TabsTrigger value="maturity">Maturidade</TabsTrigger>
                    <TabsTrigger value="footer">Footer</TabsTrigger>
                </TabsList>

                <TabsContent value="hero">
                    <SectionForm title="Hero" data={hero} onSave={d => adminApi.updateHero(d)}
                        fields={[
                            { key: 'overline', label: 'Overline' },
                            { key: 'title', label: 'Título' },
                            { key: 'subtitle', label: 'Subtítulo' },
                            { key: 'description', label: 'Descrição', type: 'textarea' },
                            { key: 'label_button', label: 'Texto do Botão' },
                            { key: 'url_button', label: 'URL do Botão' },
                            { key: 'image_alt', label: 'Alt da Imagem' },
                            { key: 'image', label: 'Imagem (ID ou URL)' },
                        ]}
                    />
                </TabsContent>

                <TabsContent value="benefits">
                    <SectionForm title="Seção Benefícios" data={benefits} onSave={d => adminApi.updateBenefits(d)}
                        fields={[
                            { key: 'overline', label: 'Overline' },
                            { key: 'title', label: 'Título' },
                            { key: 'description', label: 'Descrição', type: 'textarea' },
                        ]}
                    />
                    <ItemsList title="Cards de Benefício" items={benefits?.home_benefits_cards || []}
                        fields={[
                            { key: 'title', label: 'Título' },
                            { key: 'description', label: 'Descrição', type: 'textarea' },
                            { key: 'image_alt', label: 'Alt da Imagem' },
                            { key: 'label_button', label: 'Texto do Botão' },
                            { key: 'order', label: 'Ordem', type: 'number' },
                        ]}
                        onSave={adminApi.upsertBenefitCard} onDelete={adminApi.deleteBenefitCard} parentId={benefits?.id}
                    />
                </TabsContent>

                <TabsContent value="faq">
                    <SectionForm title="Seção FAQ" data={faq} onSave={d => adminApi.updateFaq(d)}
                        fields={[
                            { key: 'overline', label: 'Overline' },
                            { key: 'title', label: 'Título' },
                            { key: 'description', label: 'Descrição', type: 'textarea' },
                        ]}
                    />
                    <ItemsList title="Perguntas Frequentes" items={faq?.home_faq_questions || []}
                        fields={[
                            { key: 'question', label: 'Pergunta' },
                            { key: 'answer', label: 'Resposta', type: 'textarea' },
                            { key: 'order', label: 'Ordem', type: 'number' },
                        ]}
                        onSave={adminApi.upsertFaqQuestion} onDelete={adminApi.deleteFaqQuestion} parentId={faq?.id}
                    />
                </TabsContent>

                <TabsContent value="how">
                    <SectionForm title="Seção Como Funciona" data={howItWorks} onSave={d => adminApi.updateHowItWorks(d)}
                        fields={[
                            { key: 'overline', label: 'Overline' },
                            { key: 'title', label: 'Título' },
                            { key: 'description', label: 'Descrição', type: 'textarea' },
                        ]}
                    />
                    <ItemsList title="Cards" items={howItWorks?.home_how_it_works_card || []}
                        fields={[
                            { key: 'title', label: 'Título' },
                            { key: 'description', label: 'Descrição', type: 'textarea' },
                            { key: 'overline', label: 'Overline' },
                            { key: 'order', label: 'Ordem', type: 'number' },
                        ]}
                        onSave={adminApi.upsertHowItWorksCard} onDelete={adminApi.deleteHowItWorksCard} parentId={howItWorks?.id}
                    />
                </TabsContent>

                <TabsContent value="maturity">
                    <SectionForm title="Seção Explicação de Maturidade" data={maturity} onSave={d => adminApi.updateMaturityExplanation(d)}
                        fields={[
                            { key: 'overline', label: 'Overline' },
                            { key: 'title', label: 'Título' },
                            { key: 'description', label: 'Descrição', type: 'textarea' },
                        ]}
                    />
                    <ItemsList title="Cards de Maturidade" items={maturity?.home_maturity_explanation_cards || []}
                        fields={[
                            { key: 'title', label: 'Título' },
                            { key: 'description', label: 'Descrição', type: 'textarea' },
                            { key: 'overline', label: 'Overline' },
                            { key: 'image_alt', label: 'Alt da Imagem' },
                            { key: 'order', label: 'Ordem', type: 'number' },
                        ]}
                        onSave={adminApi.upsertMaturityCard} onDelete={adminApi.deleteMaturityCard} parentId={maturity?.id}
                    />
                </TabsContent>

                <TabsContent value="footer">
                    <SectionForm title="Footer" data={footer} onSave={d => adminApi.updateFooter(d)}
                        fields={[{ key: 'title', label: 'Título' }]}
                    />
                    <ItemsList title="Menu do Footer" items={footer?.home_footer_menu || []}
                        fields={[
                            { key: 'title', label: 'Título' },
                            { key: 'url', label: 'URL' },
                            { key: 'order', label: 'Ordem', type: 'number' },
                        ]}
                        onSave={adminApi.upsertFooterMenu} onDelete={adminApi.deleteFooterMenu} parentId={footer?.id}
                    />
                    <ItemsList title="Redes Sociais" items={footer?.home_footer_social || []}
                        fields={[
                            { key: 'title', label: 'Nome' },
                            { key: 'url', label: 'URL' },
                            { key: 'icon', label: 'Ícone' },
                            { key: 'order', label: 'Ordem', type: 'number' },
                        ]}
                        onSave={adminApi.upsertFooterSocial} onDelete={adminApi.deleteFooterSocial} parentId={footer?.id}
                    />
                </TabsContent>
            </Tabs>
        </div>
    );
}
